export type GamePhase = 'none' | 'lobby' | 'playing' | 'judging' | 'roundResult' | 'gameOver';

export interface GamePlayer {
  id: string;
  name: string;
  initials: string;
  score: number;
  isHost: boolean;
  isReady: boolean;
  isConnected: boolean;
  gradient: string;
}

export interface HandCard {
  id: string;
  text: string;
  type: 'white' | 'wild';
}

export interface Submission {
  id: string;
  playerId: string;
  text: string;
  /** Present for server anonymized submissions during judging. */
  revealIndex?: number;
}

export interface GameSettings {
  roomName: string;
  maxPlayers: number;
  pointsToWin: number;
  roundTimer: boolean;
  wildCardsEnabled: boolean;
  privateRoom: boolean;
}

export interface GameLogMessage {
  id: string;
  author?: string;
  text: string;
  self?: boolean;
  system?: boolean;
}

export interface RoundResult {
  winnerId: string;
  answerText: string;
  promptText: string;
}

/** One entry in the visible winning-cards history (safe, public data only). */
export interface WinLogEntry {
  roundNumber: number;
  promptText: string;
  answerText: string;
  winnerId: string;
  winnerName: string;
}

export interface MockGameState {
  roomCode: string | null;
  currentPlayerId: string | null;
  players: GamePlayer[];
  judgeId: string | null;
  settings: GameSettings;
  phase: GamePhase;
  roundNumber: number;
  maxRounds: number;
  blackCardText: string | null;
  /** Required white cards for the current black card (from server snapshot or mock heuristic). */
  blackCardPick: number;
  hand: HandCard[];
  /** Ordered selected card ids for the current submission. */
  selectedCardIds: string[];
  /** @deprecated Use selectedCardIds — first selected id for legacy single-select reads. */
  selectedCardId: string | null;
  writeInText: string;
  submissions: Submission[];
  lastRoundResult: RoundResult | null;
  /** Public history of round winners for the in-game win-log drawer. */
  roundHistory: WinLogEntry[];
  /** How many submissions are in for the current round (for activity feed). */
  submittedCount?: number;
  log: GameLogMessage[];
  joinError: string | null;
  /** Server snapshot permissions (undefined in mock mode — pages derive locally). */
  canStartGame?: boolean;
  canSubmit?: boolean;
  canPickWinner?: boolean;
  canAdvance?: boolean;
  viewerHasSubmitted?: boolean;
  writeInMaxLength?: number;
  /** Set (server mode) when the game ended without a points winner. */
  endedReason?: 'notEnoughPlayers';
  /** Server mode: viewer joined after the current round began and plays next round. */
  joinedMidRound?: boolean;
}

export type MockGameAction =
  | { type: 'CREATE_ROOM'; payload: { roomName: string; nickname: string; settings: Partial<GameSettings> } }
  | { type: 'JOIN_ROOM'; payload: { roomCode: string; nickname: string } }
  | { type: 'CLEAR_JOIN_ERROR' }
  | { type: 'TOGGLE_READY' }
  | { type: 'START_GAME' }
  | { type: 'TOGGLE_CARD'; payload: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SELECT_CARD'; payload: string | null }
  | { type: 'SET_WRITE_IN'; payload: string }
  | { type: 'CONFIRM_WRITE_IN' }
  | { type: 'SUBMIT_ANSWER' }
  | { type: 'PICK_WINNER'; payload: string }
  | { type: 'NEXT_ROUND' }
  | { type: 'PLAY_AGAIN' }
  | { type: 'NEW_ROOM' }
  | { type: 'RESET' };

export const DEFAULT_SETTINGS: GameSettings = {
  roomName: 'Midnight Chaos',
  maxPlayers: 8,
  pointsToWin: 5,
  roundTimer: true,
  wildCardsEnabled: true,
  privateRoom: true,
};

export const INITIAL_MOCK_GAME_STATE: MockGameState = {
  roomCode: null,
  currentPlayerId: null,
  players: [],
  judgeId: null,
  settings: DEFAULT_SETTINGS,
  phase: 'none',
  roundNumber: 0,
  maxRounds: 15,
  blackCardText: null,
  blackCardPick: 1,
  hand: [],
  selectedCardIds: [],
  selectedCardId: null,
  writeInText: '',
  submissions: [],
  lastRoundResult: null,
  roundHistory: [],
  log: [],
  joinError: null,
};
